import React from "react";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { stringify } from "qs";
import { Cruise } from "main/javascripts/types/cruise";
import { AButton } from "main/javascripts/components/button/AButton";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  textColor,
  borderColor,
} from "main/javascripts/styles/base/colorStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";

export interface IProps {
  salePrice: Cruise.SalePrice;
  uniqueId: string;
  departureDate: string;
}

export const SaleCruisePriceListItem: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { salePrice, uniqueId, departureDate } = props;
  const { t } = useTranslation(["label", "component"]);

  const params = {
    departure_date: departureDate,
    cabin_code: salePrice.vesselCabinCode,
  };
  const paramsString = stringify(params);
  const showPagePath = `/cruises/${uniqueId}?${paramsString}`;

  return (
    <div css={itemStyle}>
      <div css={contentStyle}>
        <div>
          <div css={saleTextStyle}>{salePrice.saleText}</div>
          <div css={cabinNameStyle}>
            {salePrice.vesselCabinName} ({salePrice.vesselCabinCode})
          </div>
        </div>
        <div css={priceButtonBlockStyle}>
          <div css={priceBlockStyle}>
            <span css={priceStyle}>
              <span css={priceValueStyle}>
                {salePrice.priceWithoutTaxPerPerson}
              </span>
              / <span>{t("unit:person", { count: 1 })}</span>
            </span>
            <div css={priceNoteStyle}>
              {t("component:cruise.portFeeExcludedNote")}
            </div>
          </div>
          <div css={buttonBlockStyle}>
            <AButton
              type="button"
              styleKey="buttonSmall"
              href={showPagePath}
              target="_blank"
              rel="noopener"
            >
              {t("label:common.showDetail")}
            </AButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const itemStyle = css`
  padding: ${space.atom2x};
  &:not(:last-child) {
    border-bottom: 1px solid ${borderColor.primaryLightColor};
  }
  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom1_5x} ${space.atom2_5x};
  }
`;

const contentStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${space.atom1_5x} ${space.atom2_5x};
`;
const saleTextStyle = css`
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${accentColor.primaryColor};
  font-weight: ${fontWeight.medium};
  margin-bottom: ${space.atom0_5x};
`;
const cabinNameStyle = css`
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${textColor.primaryDarkColor};
`;
const priceButtonBlockStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${space.atom} ${space.atom2_5x};
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
`;
const priceBlockStyle = css`
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${textColor.primaryDarkColor};
`;
const priceStyle = css`
  display: flex;
  gap: ${space.atom0_5x};
  align-items: flex-end;
  justify-content: flex-end;
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  min-width: 10.5rem;
`;
const priceValueStyle = css`
  font-size: ${fontSize.headline};
  line-height: ${lineHeight.headline1};
  font-weight: ${fontWeight.medium};
  font-variant-numeric: tabular-nums;
`;
const priceNoteStyle = css`
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  color: ${textColor.secondaryDarkColor};
  opacity: 0.7;
  text-align: right;
`;
const buttonBlockStyle = css`
  min-width: 8rem;
`;
